import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext } from '@urbanx/agx.auth.client';
import { SAVE_BUTTON_STATES } from '@urbanx/agx.ui-components2';
import { deleteFile } from 'services';
import { AgxToastState } from 'types/commonTypes';
import {
  StaffGetBlankContractDetailsForCampaignResponseBlankSalesContractStatus,
  StaffUpdateBlankContractDetailsForCampaignRequestBlankSalesContractStatus,
  StaffUpdateBlankContractDetailsForCampaignRequest,
  UploadedDocument,
  useInvalidateUseStaffGetBlankContractDetailsForCampaign,
  useInvalidateUseStaffGetPropertyInformationProgressForCampaign,
  useStaffGetBlankContractDetailsForCampaign,
  useStaffUpdateBlankContractDetailsForCampaign,
  CampaignDetailModel,
} from '@urbanx/agx.campaigns.client';

interface UseContractSectionStateArgs {
  campaign?: CampaignDetailModel | undefined;
  updateToastState: (toastState: AgxToastState) => void;
}

interface ContractSectionState {
  contractDocument?: UploadedDocument | undefined;
  isPending: boolean;
  isError: boolean;
  pendingDelete: boolean;
  setPendingDelete: (value: boolean) => void;
  isFinalEnabled: boolean;
  isFinalSelected: boolean;
  setIsFinalSelected: (value: boolean) => void;
  hasUnsavedChanges: boolean;
  saveState: (typeof SAVE_BUTTON_STATES)[keyof typeof SAVE_BUTTON_STATES];
  handleSaveChanges: () => Promise<void>;
}

const useContractSectionState = ({
  campaign,
  updateToastState,
}: UseContractSectionStateArgs): ContractSectionState => {
  const { getAuthToken } = useContext(AuthContext);
  const [pendingDelete, setPendingDelete] = useState(false);
  const [isFinalSelected, setIsFinalSelected] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasSaved, setHasSaved] = useState(false);
  const [hasError, setHasError] = useState(false);

  const {
    data: blankContractDetails,
    isPending,
    isError,
  } = useStaffGetBlankContractDetailsForCampaign(
    campaign?.campaignId ?? '',
    !!campaign?.campaignId
  );

  const invalidateStaffGetBlankContractDetails =
    useInvalidateUseStaffGetBlankContractDetailsForCampaign();
  const invalidateStaffGetPropertyInformationProgress =
    useInvalidateUseStaffGetPropertyInformationProgressForCampaign();

  const { staffUpdateBlankContractDetailsForCampaign } =
    useStaffUpdateBlankContractDetailsForCampaign({
      onSuccess: () => undefined,
      onError: () => undefined,
    });

  useEffect(() => {
    setIsFinalSelected(
      blankContractDetails?.blankSalesContractStatus ===
        StaffGetBlankContractDetailsForCampaignResponseBlankSalesContractStatus.Final
    );
  }, [blankContractDetails]);

  const selectedStatus = useMemo(() => {
    return isFinalSelected
      ? StaffGetBlankContractDetailsForCampaignResponseBlankSalesContractStatus.Final
      : StaffGetBlankContractDetailsForCampaignResponseBlankSalesContractStatus.Draft;
  }, [isFinalSelected]);

  const selectedUpdateStatus = useMemo(() => {
    return isFinalSelected
      ? StaffUpdateBlankContractDetailsForCampaignRequestBlankSalesContractStatus.Final
      : StaffUpdateBlankContractDetailsForCampaignRequestBlankSalesContractStatus.Draft;
  }, [isFinalSelected]);

  const isFinalEnabled = useMemo(() => {
    return (
      blankContractDetails?.blankSalesContractStatus ===
      StaffGetBlankContractDetailsForCampaignResponseBlankSalesContractStatus.Final
    );
  }, [blankContractDetails]);

  const hasUnsavedChanges = useMemo(() => {
    return (
      pendingDelete ||
      (blankContractDetails?.blankSalesContractStatus ??
        StaffGetBlankContractDetailsForCampaignResponseBlankSalesContractStatus.Draft) !==
        selectedStatus
    );
  }, [blankContractDetails, selectedStatus, pendingDelete]);

  useEffect(() => {
    setPendingDelete(false);
  }, [blankContractDetails?.blankSalesContract]);

  useEffect(() => {
    if (hasUnsavedChanges) {
      setHasSaved(false);
      setHasError(false);
    }
  }, [hasUnsavedChanges]);

  const saveState = useMemo(() => {
    if (isSaving) return SAVE_BUTTON_STATES.SAVING;
    if (hasError) return SAVE_BUTTON_STATES.ERROR;
    if (hasSaved) return SAVE_BUTTON_STATES.SAVED;
    return SAVE_BUTTON_STATES.UNSAVED;
  }, [hasError, hasSaved, isSaving]);

  const invalidateContractQueries = useCallback(async () => {
    await Promise.all([
      invalidateStaffGetBlankContractDetails(campaign?.campaignId || ''),
      invalidateStaffGetPropertyInformationProgress(campaign?.campaignId || ''),
    ]);
  }, [
    campaign?.campaignId,
    invalidateStaffGetBlankContractDetails,
    invalidateStaffGetPropertyInformationProgress,
  ]);

  const updateBlankContractDetails = useCallback(
    async (body: StaffUpdateBlankContractDetailsForCampaignRequest) => {
      await staffUpdateBlankContractDetailsForCampaign({
        campaignId: campaign?.campaignId || '',
        body,
      });
    },
    [campaign?.campaignId, staffUpdateBlankContractDetailsForCampaign]
  );

  const handleSaveChanges = useCallback(async () => {
    if (!hasUnsavedChanges || isSaving) return;
    setIsSaving(true);
    setHasError(false);

    try {
      const authToken = await getAuthToken();
      const deletePromise =
        pendingDelete && blankContractDetails?.blankSalesContract?.containerFilePath && authToken
          ? deleteFile(authToken, blankContractDetails.blankSalesContract.containerFilePath)
          : Promise.resolve();

      await Promise.all([
        deletePromise,
        updateBlankContractDetails({
          blankSalesContractStatus: selectedUpdateStatus,
          blankSalesContract: pendingDelete ? undefined : blankContractDetails?.blankSalesContract,
        }),
      ]);
      await invalidateContractQueries();
      setHasSaved(true);
      setPendingDelete(false);
      updateToastState({
        color: 'success',
        message: pendingDelete ? 'Contract deleted successfully' : 'Contract saved successfully',
        open: true,
      });
    } catch {
      setHasError(true);
      updateToastState({
        color: 'error',
        message: 'Error saving contract',
        open: true,
      });
    } finally {
      setIsSaving(false);
    }
  }, [
    blankContractDetails?.blankSalesContract,
    getAuthToken,
    hasUnsavedChanges,
    invalidateContractQueries,
    isSaving,
    pendingDelete,
    selectedUpdateStatus,
    updateBlankContractDetails,
    updateToastState,
  ]);

  return {
    contractDocument: blankContractDetails?.blankSalesContract,
    isPending,
    isError,
    pendingDelete,
    setPendingDelete,
    isFinalEnabled,
    isFinalSelected,
    setIsFinalSelected,
    hasUnsavedChanges,
    saveState,
    handleSaveChanges,
  };
};

export default useContractSectionState;
